import Tippy from '@tippyjs/react';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { friendlyAssessmentNatureName } from '@upright/common/model/input/types';
import { ApiPaiValue, ApiPaiValues } from '@upright/common/model/output/eu-regulation/resolved-pais/types';
import { getTitleStyle } from '@upright/ui/common/generic/content/texts/bou-title/title-style';
import {
  BouSpacingLevel,
  fluidHorizontalPadding,
  fluidSpacing,
  fluidVerticalPadding,
} from '@upright/ui/common/generic/layout/bou-spacing';
import { get } from 'lodash';
import { ReactNode, RefObject } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  FinalImpactScoreType,
  ImpactScoresByTIVV,
  ImpactScoresByVV,
} from '../../../../../../../../../../../common/model/output/scores/types';
import { Valence } from '../../../../../../../../../../../common/model/types';
import { friendlyImpactTitle } from '../../../../../../../../../../../common/utils';
import { getImpactSizeFactors } from '../../../../../../../../../../untidy/redux/selectors';
import { shortenedBigNumber } from '../../../../../../../../../../untidy/texts';
import { uprightTippyStyling } from '../../../../../../../../../generic/components/tooltips/tippy-styles/upright';
import { BouColor } from '../../../../../../../../../generic/global/bou-colors';
import { useIsTeam } from '../../../../../../../../../generic/hooks';
import { formatWithTwoDecimalsGrayNA } from '../../../../../../../../../generic/utilities/format';
import type { Leena2Interval } from '../../../../../../../../../../../common/model/output/scores/leena2-intervals';
import { useImpactChartContext } from '../../../../context';
import { formatImpactScore } from '../../../../value-formatter';

export function ImpactBarTooltip(props: ImpactBarTooltipProps) {
  const { relativeMode, formatters, scoreType } = useImpactChartContext();
  const impactSizeFactors = useSelector(getImpactSizeFactors);
  const { data, valence, impactCode, barReference, interval, showDetails = true } = props;

  // Temporary: only enable for team
  const isTeam = useIsTeam();

  if (!isTeam) {
    return null;
  }

  const details = showDetails ? detailsContent() : null;
  // Leena-2 Clef results with "Show Leena-2 uncertainty" on: the bar's 80%
  // interval, formatted like the bar's own value label.
  const intervalLine = interval ? (
    <IntervalLine>
      80% interval: {formatImpactScore(interval.p10, formatters, scoreType)}–
      {formatImpactScore(interval.p90, formatters, scoreType)}
    </IntervalLine>
  ) : null;

  if (!details && !intervalLine) {
    return null;
  }

  return (
    <foreignObject>
      <StyledTippy
        content={
          <TooltipWrap>
            {details}
            {intervalLine}
          </TooltipWrap>
        }
        reference={barReference}
        duration={100}
        trigger="mouseenter"
        arrow={true}
      />
    </foreignObject>
  );

  function detailsContent(): ReactNode {
    if (!impactSizeFactors) {
      return null;
    }

    const economicCost = get(impactSizeFactors, [impactCode, valence], undefined)?.economicCost;

    if (!relativeMode) {
      if (impactCode !== ImpactCategoryId.GHG_EMISSIONS) {
        // Currently only render for GHG emissions
        return null;
      }

      const absoluteScores = data?.[FinalImpactScoreType.ABSOLUTE]?.[impactCode];
      if (!absoluteScores || !economicCost) {
        // Not all impact codes have positive and negative valence impact...
        return null;
      }

      return tooltipContentAbsolute(valence, impactCode, props.euPaiMetrics);
    }

    const relativeScores = data?.[FinalImpactScoreType.RELATIVE]?.[impactCode];

    if (!relativeScores || !economicCost) {
      // Not all impact codes have positive and negative valence impact...
      return null;
    }

    const relScore = getTotalScore(relativeScores, valence);
    const impliedIntensity = relScore / economicCost;

    return tooltipContentRelative(relScore, impliedIntensity, economicCost, valence, impactCode);
  }
}

interface ScoreBoxProps {
  column: number;
  valence?: Valence;
  bold?: boolean;
}

const backgroundOfValence = (v?: Valence) => {
  switch (v) {
    case Valence.POSITIVE:
      return BouColor.IMPACT_POSITIVE_GREEN;
    case Valence.NEGATIVE:
      return BouColor.IMPACT_NEGATIVE_PURPLE;
    case Valence.OTHER:
    case undefined:
      return BouColor.IMPACT_NEUTRAL_GRAY;
  }
};

const foregroundOfValence = (v?: Valence) => {
  if (v === Valence.NEGATIVE) {
    return BouColor.WHITE;
  }
  return BouColor.BLACK;
};

export interface ImpactBarTooltipProps {
  barReference: RefObject<any>;
  data?: ImpactScoresByTIVV;
  valence: Valence;
  impactCode: ImpactCategoryId;
  euPaiMetrics?: ApiPaiValues;
  /** The intensity / PAI details (the `netImpact.barAnnotations` view
   *  setting). Defaults to true. */
  showDetails?: boolean;
  /** The bar's 80% interval in the bar's unit (Leena-2 Clef results with
   *  "Show Leena-2 uncertainty" on). */
  interval?: Leena2Interval;
}

function getTotalScore(scoresByVV: ImpactScoresByVV, valence: Valence) {
  return Object.values(scoresByVV).reduce((acc, v) => {
    return acc + (valence in v ? v[valence].value : 0);
  }, 0);
}

function tooltipContentAbsolute(
  valence: Valence,
  impactCode: ImpactCategoryId,
  euPaiMetrics: ApiPaiValues | undefined,
) {
  return (
    <TooltipWrap>
      <AbsTooltipWrap>
        {valence === Valence.NEGATIVE && impactCode === ImpactCategoryId.GHG_EMISSIONS && (
          <div>
            <PaiMetricsTitle>Ghg emissions</PaiMetricsTitle>
            <PaiMetricRow title="Scope 1" pai={euPaiMetrics?.GHG1} />
            <PaiMetricRow title="Scope 2" pai={euPaiMetrics?.GHG2} />
            <PaiMetricRow title="Scope 3" pai={euPaiMetrics?.GHG3} />
          </div>
        )}
      </AbsTooltipWrap>
    </TooltipWrap>
  );
}

interface PaiMetricRowProps {
  title: string;
  pai?: ApiPaiValue;
}
function PaiMetricRow({ title, pai }: PaiMetricRowProps) {
  const formatValue = (value: number) => (
    <div>
      {shortenedBigNumber(value, 1, true)}t&nbsp;CO<sub>2</sub>&#8209;eq;
    </div>
  );
  return (
    <Row>
      <Label>{title}</Label>
      <ValueSection>
        <div>{pai ? formatValue(pai.value) : <NoValue />}</div>
        <div>{pai ? <Nature>({friendlyAssessmentNatureName(pai.nature)})</Nature> : ''}</div>
      </ValueSection>
    </Row>
  );
}

function NoValue() {
  return <div style={{ color: BouColor.TOOL_TEXT_DARK_GRAY }}>N/A</div>;
}

const AbsTooltipWrap = styled.div`
  ${fluidVerticalPadding(BouSpacingLevel.MICRO)}
  ${fluidHorizontalPadding(BouSpacingLevel.MICRO)}
`;

const PaiMetricsTitle = styled.div`
  ${getTitleStyle({ size: 'h6', withMargin: false })}
  ${fluidSpacing('margin-top', BouSpacingLevel.MICRO)}
  ${fluidSpacing('margin-bottom', BouSpacingLevel.TINY)}
`;

const Row = styled.div`
  display: flex;
  ${fluidSpacing('padding-top', BouSpacingLevel.MICRO)}
  ${fluidSpacing('padding-bottom', BouSpacingLevel.TINY)}
`;
const Label = styled.div`
  font-weight: bold;
  flex-grow: 1;
  vertical-align: top;
  ${fluidSpacing('margin-right', BouSpacingLevel.MICRO)}
`;
const ValueSection = styled.div`
  flex-grow: 2.5;
`;
const Nature = styled.div`
  font-style: italic;
  color: ${BouColor.TOOL_TEXT_DARK_GRAY};
`;

function tooltipContentRelative(
  relScore: number,
  intensity: number,
  economicCost: number,
  valence: Valence,
  impactCode: string,
) {
  const impactTitle = friendlyImpactTitle(impactCode);
  return (
    <TooltipWrap>
      <TooltipGridWrap>
        <ColTitle column={1}>Intensity</ColTitle>
        <ColTitle column={3}>Weight</ColTitle>
        <ColTitle column={5}>Value</ColTitle>
        <ScoreBox column={1} valence={valence} bold>
          {valence === Valence.NEGATIVE && '-'}
          {formatWithTwoDecimalsGrayNA(intensity)}
        </ScoreBox>
        <MathOperator column={2}>{'\u00D7'}</MathOperator>
        <ScoreBox column={3}>{formatWithTwoDecimalsGrayNA(economicCost)}</ScoreBox>
        <MathOperator column={4}>=</MathOperator>
        <ScoreBox column={5} valence={valence} bold>
          {valence === Valence.NEGATIVE && '-'}
          {formatWithTwoDecimalsGrayNA(relScore)}
        </ScoreBox>
        <ColDescription column={1}>Impact relative to an average company</ColDescription>
        <ColDescription column={3}>Economic cost multiplier for {impactTitle}</ColDescription>
        <ColDescription column={5}>Normalized impact value for comparisons</ColDescription>
      </TooltipGridWrap>
    </TooltipWrap>
  );
}

const StyledTippy = styled(Tippy)`
  max-width: 100vw !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  line-height: 22px;
  font-size: 14px;
  text-align: left;
  &:focus {
    outline: none;
  }
  ${uprightTippyStyling}
`;

const TooltipWrap = styled.div``;

const IntervalLine = styled.div`
  font-size: 12px;
  color: ${BouColor.TOOL_TEXT_DARK_GRAY};
  white-space: nowrap;
`;

const TooltipGridWrap = styled.div`
  display: grid;
  width: 26vw;
`;

const TooltipCell = styled.div`
  margin: 5px;
`;

const ColTitle = styled(TooltipCell)`
  grid-row: 1;
  grid-column: ${(p: { column: number }) => p.column};
  text-transform: uppercase;
  font-weight: bold;
`;

const ScoreBox = styled(TooltipCell)`
  background-color: ${(p: ScoreBoxProps) => backgroundOfValence(p.valence)};
  color: ${(p: ScoreBoxProps) => foregroundOfValence(p.valence)};
  line-height: 30px;
  text-align: center;
  grid-row: 2;
  grid-column: ${(p: ScoreBoxProps) => p.column};
  font-weight: ${(p: ScoreBoxProps) => (p.bold ? 'bold' : 'inherit')};
  border-radius: 5px;
`;

const ColDescription = styled(TooltipCell)`
  grid-row: 3;
  grid-column: ${(p: { column: number }) => p.column};
  color: ${BouColor.TOOL_TEXT_DARK_GRAY};
  font-size: 12px;
`;

const MathOperator = styled(TooltipCell)`
  line-height: 30px;
  grid-row: 2;
  grid-column: ${(p: { column: number }) => p.column};
  color: ${BouColor.TOOL_TEXT_DARK_GRAY};
  text-transform: uppercase;
  font-size: 24px;
`;
